import {useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  getAttendanceWindowGroups, getAttendanceWindows, updateAttendanceWindow
} from '../../API/attendanceWindows';
import {formatDateInBogota, formatDateTimeInBogota} from '../../utils/dateTime';
import {closingTime, toBogotaInput} from './dateTime';
import './style.scss';

export default function SuperadminAttendanceWindows() {
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    let active = true;
    getAttendanceWindowGroups()
      .then(data => { if (active) setGroups(data); })
      .catch(() => { if (active) setError('No fue posible cargar los grupos. Recarga la página para intentarlo nuevamente.'); })
      .finally(() => { if (active) setLoadingGroups(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setMeetings([]);
    setEditor(null);
    setNotice('');
    if (!group) { setLoading(false); return () => { active = false; }; }
    setError('');
    setLoading(true);
    getAttendanceWindows(group)
      .then(data => { if (active) setMeetings(data); })
      .catch(() => { if (active) setError('No fue posible cargar los encuentros. Selecciona el grupo nuevamente.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [group]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('es');
    return groups.filter(item =>
      `${item.code} ${item.entity} ${item.mediator} ${item.kind}`.toLocaleLowerCase('es').includes(query)
    );
  }, [groups, search]);
  const selectedGroup = groups.find(item => item.key === group);
  const durationHours = Number(editor?.meeting?.durationHours) || 2;
  const closesAt = closingTime(editor?.value, durationHours);

  async function save(event) {
    event.preventDefault();
    if (!editor || !closesAt || busy.current) return;
    busy.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await updateAttendanceWindow(editor.meeting.id, editor.value);
      setMeetings(current => current.map(meeting => meeting.id === updated.id ? updated : meeting));
      setEditor(null);
      setNotice(`Plazo actualizado. Las firmas cierran el ${formatDateTimeInBogota(updated.closesAt)} (hora de Colombia).`);
    } catch (err) {
      const code = err?.response?.data?.message;
      setError(code === 'INVALID_ATTENDANCE_START'
        ? 'Ingresa una fecha y hora válidas.'
        : code === 'SUPERADMIN_REQUIRED'
          ? 'Solo el superadmin puede modificar el plazo de firmas.'
          : 'No se pudo guardar el cambio. El valor ingresado se conserva para volver a intentarlo.');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }

  return <section className='attendance-windows'>
    <div className='attendance-windows__content'>
      <Link to='/superadmin'>Volver al panel de superadmin</Link>
      <h1>Plazos de firmas</h1>
      <p>Selecciona un grupo y edita el inicio del plazo de firmas de un encuentro. El cierre ocurre 2 horas después del valor guardado, o 24 horas para diplomados.</p>
      <p>Todas las fechas y horas se muestran en hora de Colombia (UTC−5).</p>

      <div className='attendance-windows__filters'>
        <label>Buscar grupo, entidad o mediador
          <input type='search' value={search} disabled={saving || loadingGroups}
            onChange={event => { setSearch(event.target.value); setGroup(''); }} />
        </label>
        <label>Grupo o asesoría
          <select value={group} disabled={saving || loadingGroups}
            onChange={event => setGroup(event.target.value)}>
            <option value=''>{loadingGroups ? 'Cargando grupos...' : 'Selecciona un grupo'}</option>
            {filteredGroups.map(item => <option key={item.key} value={item.key}>
              {item.code} — {item.kind}{item.entity ? ` — ${item.entity}` : ''}{item.mediator ? ` — ${item.mediator}` : ''}
            </option>)}
          </select>
        </label>
      </div>
      {!loadingGroups && filteredGroups.length === 0 && <p>No hay grupos que coincidan con la búsqueda.</p>}
      {error && <p className='attendance-windows__error' role='alert'>{error}</p>}
      {notice && <p className='attendance-windows__notice' role='status'>{notice}</p>}
      {loading && <p role='status'>Cargando encuentros...</p>}
      {!loading && group && <>
        <h2>Encuentros de {selectedGroup?.code}</h2>
        {meetings.length === 0 ? <p>No hay encuentros registrados para este grupo.</p> : <div className='attendance-windows__table'>
          <table>
            <thead><tr><th>Encuentro</th><th>Fecha de la sesión</th><th>Inicio del plazo</th><th>Cierre de firmas</th><th>Acciones</th></tr></thead>
            <tbody>{meetings.map(meeting => <tr key={meeting.id}>
              <td><strong>{meeting.type === 'COP_PLANEACION' ? 'Planeación' : meeting.type === 'COP_DESARROLLO' ? 'Desarrollo' : `Encuentro ${meeting.order}`}</strong>
                <small>ID {meeting.id}{meeting.topic ? ` · ${meeting.topic}` : ''}</small></td>
              <td>{formatDateInBogota(meeting.meetingDate) || 'Sin fecha'}<small>{meeting.startTime?.slice(0, 5)}{meeting.endTime ? ` – ${meeting.endTime.slice(0, 5)}` : ''}</small></td>
              <td>{formatDateTimeInBogota(meeting.startedAt) || 'Sin inicio registrado'}</td>
              <td>{formatDateTimeInBogota(meeting.closesAt) || `${meeting.durationHours || 2} horas desde la primera firma`}</td>
              <td><button type='button' disabled={saving}
                aria-label={`Editar plazo del encuentro ${meeting.order}, ID ${meeting.id}`}
                onClick={() => {
                  setError(''); setNotice('');
                  setEditor({meeting, value: toBogotaInput(meeting.startedAt || new Date().toISOString())});
                }}>Editar plazo</button></td>
            </tr>)}</tbody>
          </table>
        </div>}
      </>}

      {editor && <form className='attendance-windows__editor' onSubmit={save}>
        <h2>Editar encuentro {editor.meeting.order} · {selectedGroup?.code} · ID {editor.meeting.id}</h2>
        <p>Inicio actual: {formatDateTimeInBogota(editor.meeting.startedAt) || 'Sin inicio registrado'}.</p>
        <label>Nuevo inicio del plazo de firmas (hora de Colombia)
          <input type='datetime-local' step='1' required value={editor.value} disabled={saving}
            onChange={event => setEditor({...editor, value: event.target.value})} />
        </label>
        <p aria-live='polite'>Cierre resultante: <strong>{closesAt ? formatDateTimeInBogota(closesAt) : 'Selecciona una fecha y hora válidas'}</strong>.</p>
        <p>Este encuentro tiene una ventana de {durationHours} horas. Para habilitar 2 horas desde ahora, indica una hora de inicio {durationHours - 2} horas anterior a la actual.</p>
        <div className='attendance-windows__actions'>
          <button type='submit' disabled={saving || !closesAt}>{saving ? 'Guardando...' : 'Guardar cambio'}</button>
          <button type='button' disabled={saving} onClick={() => setEditor(null)}>Cancelar</button>
        </div>
      </form>}
    </div>
  </section>;
}
